import React, { useState } from 'react';
import PageSEO from '../components/PageSEO';
import WebContactForm from '../components/WebContactForm';
import { ImageLightboxModal, ClickableWorkImage } from '../components/GalleryLightbox';
import { SITE_INFO, BRAND_ASSETS } from '../data';

export default function UdlejningPage() {
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });

  const rentalImages = [
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/udlejning-jernkoereplader.webp",
      alt: "Jernkøreplader klar til udlejning",
      caption: "Jernkøreplader klar til udlejning"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/15050-k-replader.jpg",
      alt: "Plastkøreplader",
      caption: "Plastkøreplader"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/gangbro.jpg",
      alt: "Gangbroer i aluminium",
      caption: "Gangbroer i aluminium"
    }
  ];
  return (
    <div className="bg-stone-100 min-h-screen">
      <PageSEO
        title="Udlejning af jernkøreplader og byggepladsudstyr | KTS-Entreprise A/S"
        description="KTS-Entreprise har cirka 1.000 jernkøreplader til udlejning og kan levere til både mindre opgaver og større bygge- og anlægsprojekter. Plastkøreplader, gangbroer og hegn."
        ogTitle="Udlejning af jernkøreplader og byggepladsudstyr | KTS-Entreprise A/S"
        ogDescription="Cirka 1.000 jernkøreplader til rådighed, plastkøreplader, alu-gangbroer og byggepladsudstyr til leje i 7-dages perioder."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="udlejning"
      />

      {/* Header Banner */}
      <section className="bg-stone-900 text-white pt-36 sm:pt-44 md:pt-48 lg:pt-52 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>Udlejning</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white font-heading">
            Udlejning af jernkøreplader og byggepladsudstyr
          </h1>
          <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-2xl">
            KTS-Entreprise har cirka 1.000 jernkøreplader til udlejning og kan levere til både mindre opgaver og større bygge- og anlægsprojekter. Vi udlejer desuden plastkøreplader, gangbroer, autoklodser, afspærring og byggepladshegn.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        {/* 1. Primær Sektion: JERNKØREPLADER */}
        <section className="bg-white border-2 border-[#0072c0] p-6 sm:p-10 rounded-xs shadow-md mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-7 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs font-heading font-bold uppercase tracking-wider bg-[#0072c0] text-white px-3 py-1 rounded-xs">
                  Vigtigste produktkategori
                </span>
                <span className="text-xs font-heading font-bold uppercase tracking-wider bg-stone-100 text-stone-700 px-3 py-1 rounded-xs border border-stone-200">
                  Udlejes i perioder på syv dage
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase text-stone-900 font-heading">
                Jernkøreplader
              </h2>

              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                KTS-Entreprise råder over en omfattende beholdning af robuste jernkøreplader til sikring af byggepladser, udgravninger og transportveje.
              </p>

              <ul className="space-y-2.5 text-stone-800 text-sm sm:text-base pt-2">
                <li className="flex items-start gap-2.5 font-medium">
                  <i className="fa-solid fa-check text-[#0072c0] text-sm mt-1 shrink-0"></i>
                  <span><strong>Cirka 1.000 plader</strong> til rådighed</span>
                </li>
                <li className="flex items-start gap-2.5 font-medium">
                  <i className="fa-solid fa-check text-[#0072c0] text-sm mt-1 shrink-0"></i>
                  <span><strong>Mål:</strong> 2.000 × 3.000 mm</span>
                </li>
                <li className="flex items-start gap-2.5 font-medium">
                  <i className="fa-solid fa-check text-[#0072c0] text-sm mt-1 shrink-0"></i>
                  <span><strong>Tykkelse:</strong> 12 mm eller 14 mm</span>
                </li>
                <li className="flex items-start gap-2.5 font-medium">
                  <i className="fa-solid fa-check text-[#0072c0] text-sm mt-1 shrink-0"></i>
                  <span>Velegnede til etablering af en stabil køreflade for lastbiler, maskiner og andet tungt materiel</span>
                </li>
                <li className="flex items-start gap-2.5 font-medium">
                  <i className="fa-solid fa-check text-[#0072c0] text-sm mt-1 shrink-0"></i>
                  <span>Udlejes i perioder på syv dage</span>
                </li>
                <li className="flex items-start gap-2.5 font-medium">
                  <i className="fa-solid fa-check text-[#0072c0] text-sm mt-1 shrink-0"></i>
                  <span>Transport kan arrangeres efter aftale gennem KTS-Entreprises faste vognmand</span>
                </li>
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="border border-stone-200 bg-stone-50 p-2 rounded-xs">
                <ClickableWorkImage
                  src={rentalImages[0].src}
                  alt={rentalImages[0].alt}
                  className="w-full h-64 sm:h-72 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 0 })}
                />
              </div>
            </div>

          </div>
        </section>

        {/* 2. Plastkøreplader & Gangbroer */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          
          {/* Plastkøreplader */}
          <div className="bg-white border border-stone-200 p-6 sm:p-8 rounded-xs shadow-xs flex flex-col justify-between">
            <div className="space-y-4">
              <span className="text-xs font-heading font-bold uppercase tracking-wider bg-stone-100 text-stone-700 px-2.5 py-1 rounded-xs border border-stone-200 inline-block">
                Udlejes i perioder på syv dage
              </span>
              <h3 className="text-xl sm:text-2xl font-bold uppercase text-stone-900 font-heading">
                Plastkøreplader
              </h3>
              <ul className="space-y-2 text-xs sm:text-sm text-stone-700">
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-check text-[#0072c0] text-xs mt-1 shrink-0"></i>
                  <span><strong>Mål:</strong> 2.000 × 1.500 mm eller 3.000 × 1.500 mm</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-check text-[#0072c0] text-xs mt-1 shrink-0"></i>
                  <span>Velegnede til beskyttelse af græs, fliser og belægninger</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-check text-[#0072c0] text-xs mt-1 shrink-0"></i>
                  <span>Lette at håndtere manuelt</span>
                </li>
              </ul>
            </div>
            <div className="mt-6 pt-4 border-t border-stone-100">
              <ClickableWorkImage
                src={rentalImages[1].src}
                alt={rentalImages[1].alt}
                className="w-full h-44 object-cover rounded-xs"
                onClick={() => setLightboxState({ isOpen: true, index: 1 })}
              />
            </div>
          </div>

          {/* Gangbroer i aluminium */}
          <div className="bg-white border border-stone-200 p-6 sm:p-8 rounded-xs shadow-xs flex flex-col justify-between">
            <div className="space-y-4">
              <span className="text-xs font-heading font-bold uppercase tracking-wider bg-stone-100 text-stone-700 px-2.5 py-1 rounded-xs border border-stone-200 inline-block">
                Udlejes i perioder på syv dage
              </span>
              <h3 className="text-xl sm:text-2xl font-bold uppercase text-stone-900 font-heading">
                Gangbroer i aluminium
              </h3>
              <ul className="space-y-2 text-xs sm:text-sm text-stone-700">
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-check text-[#0072c0] text-xs mt-1 shrink-0"></i>
                  <span><strong>Længde:</strong> 2,20 eller 3,20 meter</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-check text-[#0072c0] text-xs mt-1 shrink-0"></i>
                  <span><strong>Bredde:</strong> 1 meter</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-check text-[#0072c0] text-xs mt-1 shrink-0"></i>
                  <span>Med tilkørselsrampe</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-check text-[#0072c0] text-xs mt-1 shrink-0"></i>
                  <span>Sammenklappelige og nemme at transportere</span>
                </li>
              </ul>
            </div>
            <div className="mt-6 pt-4 border-t border-stone-100">
              <ClickableWorkImage
                src={rentalImages[2].src}
                alt={rentalImages[2].alt}
                className="w-full h-44 object-cover rounded-xs"
                onClick={() => setLightboxState({ isOpen: true, index: 2 })}
              />
            </div>
          </div>

        </div>

        {/* 3. Øvrigt materiel */}
        <section className="bg-stone-900 text-white p-6 sm:p-8 rounded-xs border border-stone-800 shadow-xs mb-16">
          <div className="border-l-4 border-[#0072c0] pl-4 mb-4">
            <h3 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-white font-heading">
              Øvrigt materiel til udlejning
            </h3>
            <p className="text-stone-300 text-xs sm:text-sm mt-1">
              Alt materiel udlejes i perioder på syv dage.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
            <div className="bg-stone-950 p-4 rounded-xs border border-stone-800 flex items-center gap-3">
              <i className="fa-solid fa-road-barrier text-[#0072c0] text-xl"></i>
              <div>
                <h4 className="font-heading font-bold uppercase text-sm">Autoklodser</h4>
                <p className="text-stone-400 text-xs">Til afspærring og sikring</p>
              </div>
            </div>

            <div className="bg-stone-950 p-4 rounded-xs border border-stone-800 flex items-center gap-3">
              <i className="fa-solid fa-triangle-exclamation text-[#0072c0] text-xl"></i>
              <div>
                <h4 className="font-heading font-bold uppercase text-sm">Afspærring</h4>
                <p className="text-stone-400 text-xs">Trafik- og pladsafspærring</p>
              </div>
            </div>

            <div className="bg-stone-950 p-4 rounded-xs border border-stone-800 flex items-center gap-3">
              <i className="fa-solid fa-border-all text-[#0072c0] text-xl"></i>
              <div>
                <h4 className="font-heading font-bold uppercase text-sm">Byggepladshegn</h4>
                <p className="text-stone-400 text-xs">Mobilhegn og elementer</p>
              </div>
            </div>
          </div>
        </section>

        {/* Formular (Afsnit 14) */}
        <div className="mt-16">
          <WebContactForm
            title="Få tilbud på leje af køreplader & materiel"
            subtitle="Udfyld formularen med dine ønsker om lejeperiode og mængde, så vender vi hurtigt tilbage."
            submitText="Få tilbud på leje af køreplader"
            showAddress={false}
            customFields={
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="equipment_type" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                      Hvilket materiel ønskes lejet? <span className="text-[#0072c0]">*</span>
                    </label>
                    <select
                      id="equipment_type"
                      name="equipment_type"
                      required
                      className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                    >
                      <option value="Jernkøreplader">Jernkøreplader (2.000 × 3.000 mm)</option>
                      <option value="Plastkøreplader">Plastkøreplader</option>
                      <option value="Alu-gangbroer">Gangbroer i aluminium</option>
                      <option value="Autoklodser">Autoklodser</option>
                      <option value="Byggepladshegn / Afspærring">Byggepladshegn / Afspærring</option>
                      <option value="Flere typer materiel">Flere typer materiel</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="item_count" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                      Antal <span className="text-[#0072c0]">*</span>
                    </label>
                    <input
                      id="item_count"
                      name="item_count"
                      type="text"
                      required
                      placeholder="F.eks. 20 stk."
                      className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="start_date" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                      Ønsket startdato
                    </label>
                    <input
                      id="start_date"
                      name="start_date"
                      type="date"
                      className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="rental_period" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                      Forventet lejeperiode
                    </label>
                    <input
                      id="rental_period"
                      name="rental_period"
                      type="text"
                      placeholder="F.eks. 14 dage / 4 uger"
                      className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="delivery_address" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                    Leverings- eller arbejdsadresse
                  </label>
                  <input
                    id="delivery_address"
                    name="delivery_address"
                    type="text"
                    placeholder="Vejnavn, postnr. og by for levering"
                    className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="transport_quote" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                    Ønskes der tilbud på transport med vognmand?
                  </label>
                  <select
                    id="transport_quote"
                    name="transport_quote"
                    className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                  >
                    <option value="Ja, ønskes leveret og afhentet med vognmand">Ja, ønskes leveret og afhentet med vognmand</option>
                    <option value="Nej, afhentes selv på pladsen i Frederiksværk">Nej, afhentes selv på pladsen i Frederiksværk</option>
                  </select>
                </div>
              </div>
            }
            showFileUpload={false}
          />
        </div>

        {/* Lightbox for Udlejning */}
        <ImageLightboxModal
          isOpen={lightboxState.isOpen}
          onClose={() => setLightboxState({ ...lightboxState, isOpen: false })}
          images={rentalImages}
          currentIndex={lightboxState.index}
          onNavigate={(newIdx) => setLightboxState({ ...lightboxState, index: newIdx })}
          title="Udlejning af jernkøreplader og materiel"
        />

      </div>
    </div>
  );
}
